import { useMemo } from "react"
import type { EngagementFinanceRow } from "@/types/report"
import { format, startOfDay } from "date-fns"
import { cn, parseDateOnly } from "@/lib/utils"

interface KanbanColumn {
  key: string
  title: string
  color: string
  borderColor: string
  items: EngagementFinanceRow[]
}

function KanbanCard({ row }: { row: EngagementFinanceRow }) {
  const startFormatted = row.startDate ? format(new Date(row.startDate), "MMM d") : "—"
  const endFormatted = row.endDate ? format(new Date(row.endDate), "MMM d, yyyy") : "—"

  return (
    <article
      tabIndex={0}
      role="article"
      aria-label={`${row.engagementName ?? "Engagement"}: ${row.engagementCode ?? ""}, ${startFormatted} to ${endFormatted}`}
      className="bg-white border border-slate-200 rounded-md p-3 hover:shadow-md transition-shadow cursor-default flex gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
    >
      {/* Left status bar */}
      <div
        aria-hidden="true"
        className={cn(
          "w-[3px] rounded-full flex-shrink-0",
          row.engagementStatus === "Active" ? "bg-emerald-500" :
          row.engagementStatus === "Completed" ? "bg-blue-500" :
          row.engagementStatus === "PendingChange" ? "bg-orange-500" :
          "bg-slate-400"
        )}
      />

      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-800 truncate">
          {row.engagementName ?? "—"}
        </div>
        <div className="text-xs text-slate-500 truncate mt-0.5">
          {row.engagementCode}{row.client ? ` · ${row.client}` : ""}
        </div>
        <div className="text-xs text-slate-400 mt-1">
          {startFormatted} to {endFormatted}
        </div>
      </div>
    </article>
  )
}

interface EngagementKanbanProps {
  data: EngagementFinanceRow[]
}

export function EngagementKanban({ data }: EngagementKanbanProps) {
  const today = useMemo(() => startOfDay(new Date()), [])

  const columns = useMemo<KanbanColumn[]>(() => {
    const toDo: EngagementFinanceRow[] = []
    const inProgress: EngagementFinanceRow[] = []
    const complete: EngagementFinanceRow[] = []

    for (const row of data) {
      const start = parseDateOnly(row.startDate)
      const end = parseDateOnly(row.endDate)

      if (start && start > today) {
        toDo.push(row)
      } else if (end && end < today) {
        complete.push(row)
      } else {
        inProgress.push(row)
      }
    }

    return [
      {
        key: "todo",
        title: "To Do",
        color: "bg-blue-500",
        borderColor: "border-t-blue-500",
        items: toDo,
      },
      {
        key: "in-progress",
        title: "In Progress",
        color: "bg-amber-500",
        borderColor: "border-t-amber-500",
        items: inProgress,
      },
      {
        key: "complete",
        title: "Complete",
        color: "bg-emerald-500",
        borderColor: "border-t-emerald-500",
        items: complete,
      },
    ]
  }, [data, today])

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-slate-500 text-sm">
        No data to display on the Kanban board.
      </div>
    )
  }

  return (
    <div className="flex gap-4 h-full p-4 overflow-x-auto" role="region" aria-label="Kanban board">
      {columns.map((column) => (
        <section
          key={column.key}
          aria-label={`${column.title}: ${column.items.length} engagements`}
          className={cn(
            "flex flex-col flex-1 min-w-[280px] max-w-[400px] bg-slate-50 rounded-lg border border-slate-200 border-t-[3px] overflow-hidden",
            column.borderColor
          )}
        >
          {/* Column header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-slate-700">{column.title}</h3>
            <span className="text-xs text-slate-500 bg-white px-2 py-0.5 rounded-full border border-slate-200">
              {column.items.length}
            </span>
          </div>

          {/* Column body — scrollable */}
          <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2">
            {column.items.length === 0 ? (
              <div className="text-center text-xs text-slate-400 py-8">
                No engagements
              </div>
            ) : (
              column.items.map((row) => (
                <KanbanCard key={row.id} row={row} />
              ))
            )}
          </div>
        </section>
      ))}
    </div>
  )
}
